import { type z } from 'zod'
import {
	type BrandPricingContent,
	type PricingPlanFeature,
} from '#app/config/billing/pricingContent.types'
import { type PricingSnapshotSchema } from '#app/config/schemas/landingPageSchema'

type LandingPricingSnapshotProps = z.infer<typeof PricingSnapshotSchema>

function normalizePlanFeatures(
	features: Array<PricingPlanFeature | string> | undefined,
): PricingPlanFeature[] {
	if (!features) return []
	return features.map((feature) =>
		typeof feature === 'string'
			? {
					label: feature,
					included: true,
				}
			: feature,
	)
}

function resolvePlanBullets(
	features: PricingPlanFeature[],
	params?: {
		includeOnly?: boolean
		excludeLabels?: Set<string>
		limit?: number
	},
) {
	const {
		includeOnly = true,
		excludeLabels = new Set<string>(),
		limit = 3,
	} = params ?? {}

	return features
		.filter((feature) => feature.included === includeOnly)
		.filter((feature) => !excludeLabels.has(feature.label))
		.map((feature) => feature.label)
		.slice(0, limit)
}

export function buildLandingPricingSnapshotProps(params: {
	content: BrandPricingContent
	primaryCtaHref: string
	secondaryCtaHref?: string
}): LandingPricingSnapshotProps {
	const { content, primaryCtaHref, secondaryCtaHref = '/pricing' } = params

	const freeFeatures = normalizePlanFeatures(content.freePlan.features)
	const proFeatures = normalizePlanFeatures(content.proPlan.features)
	const freeFeatureLabels = new Set(
		freeFeatures
			.filter((feature) => feature.included)
			.map((feature) => feature.label),
	)
	const permanenceNote =
		content.modelExplainer?.cancellationNote ??
		'Tu trabajo personal no se borra. Si pausas o cancelas Pro, mantienes lo que ya has construido y puedes retomarlo cuando quieras.'

	return {
		eyebrow: 'Cómo empezar',
		title: 'Empieza gratis. Elige tus dos rutas',
		subtitle: content.hero.subtitle,
		tiers: [
			{
				badge: content.freePlan.label,
				name: content.freePlan.label,
				detail: content.freePlan.headline ?? content.freePlan.label,
				description: content.freePlan.tryDescription,
				bullets: resolvePlanBullets(freeFeatures, {
					excludeLabels: new Set(['Sistema de aprendizaje']),
				}),
			},
			{
				badge: 'Pro',
				name: 'Pro',
				detail: content.proPlan.headline ?? content.proPlan.fallback.name,
				description:
					content.includes.items[1]?.description ?? content.proPlan.framingLine,
				bullets: resolvePlanBullets(proFeatures, {
					excludeLabels: freeFeatureLabels,
				}),
				highlighted: true,
			},
		],
		permanenceNote,
		primaryCta: {
			text: content.freePlan.tryLabel,
			href: primaryCtaHref,
			variant: 'outline',
		},
		secondaryCta: {
			text: 'Ver planes',
			href: secondaryCtaHref,
			variant: 'default',
		},
		background: 'primary-light',
	}
}
